/* « Démarrer » step 4 · « Votre roue » — the walkable set of record (parcours-marchand-v2, guide/4; merchant-app review
   MA-5, MA-11): the guide's roue (step 3's gifts, the sector model's chances) in the short editor, the wheel beside.
   « Garder cette roue » saves it; a roue a campaign already uses, once changed here, shows « modifications à
   publier » until that campaign republishes (D-29), as in Cadeaux. Kept as it opened, it keeps its own state. */
import { useState } from 'react'
import { Button, Note, Skeleton, useToast } from '@/ui'
import { useGiftSummary } from '@/lib/api'
import { GUIDE_STEPS } from '@/lib/vocab'
import { getRoues, mergeTypes, setActiveRoueId, setRoues, toTenths, type Roue } from '@/lib/wheel'
import RoueCourte, { roueBlockers } from '@/routes/cadeaux/RoueCourte'
import { guideRoue } from './GuideCadeaux'
import { serverIdOf } from '@/lib/mirror'
import { toastWhenSaved, writeMark } from '@/lib/writes'

export default function GuideRoue({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const toast = useToast()
  const { data } = useGiftSummary()
  const [opened] = useState<Roue | undefined>(() => { const r = guideRoue(); return r ? toTenths(r) : undefined })
  const [draft, setDraft] = useState<Roue | undefined>(opened)
  const head = (
    <div>
      <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Démarrer · étape 4 sur 6</p>
      <h2 className="t-page" style={{ margin: '0 0 4px' }}>{GUIDE_STEPS[3].title}</h2>
      <p className="t-body" style={{ margin: 0, maxWidth: 680 }}>Les cadeaux de la roue et leurs chances. La roue s’arrête toujours sur le cadeau tiré&nbsp;: aucune case perdante.</p>
    </div>
  )
  if (!data) return <div style={{ display: 'grid', gap: 10, maxWidth: 760 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  if (!draft) return (
    <div data-testid="guide-roue" style={{ maxWidth: 760, display: 'grid', gap: 16 }}>
      {head}
      <Note tone="candour">Aucun cadeau pour l’instant — <a href="#" onClick={e => { e.preventDefault(); onBack() }}>revenez à l’étape 3</a> pour en créer un.</Note>
    </div>
  )
  const garder = () => {
    const why = roueBlockers(draft)
    if (why.length) { toast({ message: why[0] }); return }
    /* nothing changed since the page opened: the roue stays as it is, « à publier » or not */
    const changed = draft.name !== opened?.name || JSON.stringify(draft.rewards) !== JSON.stringify(opened?.rewards)
    const told = changed && draft.usedBy.length > 0
    /* 2b-2 · a roue made on step 3 may have its server id by now: the draft's first id still finds it */
    const mark = writeMark()
    if (changed) setRoues(getRoues().map(w => w.id === serverIdOf(draft.id) ? { ...draft, pending: told } : w))
    setActiveRoueId(draft.id)
    toastWhenSaved(mark, toast, told ? `✓ Roue « ${draft.name} » enregistrée — les campagnes qui l’utilisent affichent « modifications à publier ».` : `✓ Roue « ${draft.name} » enregistrée.`)
    onNext()
  }
  return (
    <div data-testid="guide-roue">
      <RoueCourte draft={draft} setDraft={setDraft} head={head} lib={mergeTypes(data.types)}
        onNewGift={() => { toast({ message: 'Ajoutez le cadeau ici, puis revenez à la roue.' }); onBack() }} />
      <div className="actions" style={{ marginTop: 20 }}>
        <Button variant="quiet" onClick={onBack}>‹ Retour</Button>
        <Button variant="primary" onClick={garder} data-testid="garder-roue">Garder cette roue&nbsp;→</Button>
      </div>
    </div>
  )
}
